import React, { useEffect } from 'react';
import { useSpotlight } from '../context/SpotlightContext';

/**
 * Full-screen overlay that dims the background and highlights the spotlight target.
 * Uses a CSS clip-path to create a "cutout" around the target element.
 */
const SpotlightOverlay = () => {
  const { isSpotlightActive, targetRect, targetElement } = useSpotlight();

  // Apply highlight styles to target element
  useEffect(() => {
    if (!targetElement) return;

    // Store original styles
    const originalPosition = targetElement.style.position;
    const originalZIndex = targetElement.style.zIndex;

    // Apply highlight styles
    targetElement.style.position = 'relative';
    targetElement.style.zIndex = '10001';
    targetElement.classList.add('spotlight-target');

    return () => {
      // Restore original styles
      targetElement.style.position = originalPosition;
      targetElement.style.zIndex = originalZIndex;
      targetElement.classList.remove('spotlight-target');
    };
  }, [targetElement]);

  if (!isSpotlightActive || !targetRect) {
    return null;
  }

  // Calculate clip-path to create cutout around target
  // Adding padding around the target element
  const padding = 8;
  const cutout = {
    top: Math.max(0, targetRect.top - padding),
    left: Math.max(0, targetRect.left - padding),
    right: Math.min(window.innerWidth, targetRect.right + padding),
    bottom: Math.min(window.innerHeight, targetRect.bottom + padding),
  };

  return (
    <>
      {/* Highlight ring around target. No dimmed backdrop: a spotlight must never
          block clicks, because staff are often mid-call when one appears. */}
      <div
        className="fixed z-[10000] pointer-events-none rounded-lg ring-4 ring-teal-500 ring-offset-2 ring-offset-transparent"
        style={{
          top: cutout.top,
          left: cutout.left,
          width: cutout.right - cutout.left,
          height: cutout.bottom - cutout.top,
        }}
      />

      {/* Add CSS for spotlight target animation */}
      <style>{`
        .spotlight-target {
          animation: spotlight-pulse 2s ease-in-out infinite;
        }

        @keyframes spotlight-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(20, 184, 166, 0);
          }
        }
      `}</style>
    </>
  );
};

export default SpotlightOverlay;
